//deps
import React, { useContext } from "react";

//internal-deps
import { ApiContext, CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS, PublicApiContext } from "@swiggy-private/checkout-core";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import { Result, Err, Ok, None, Option, Some } from "@swiggy-private/js-fp";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { useMountedRef, useEffectOnce, captureError } from "@swiggy-private/core-ui";
import { LINK_ACCOUNT_SOURCE } from "@swiggy-private/native-bridge";

//apis
import { linkWallet, validateOtp } from "@payment-client/apis/Wallet";

//constants
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

//helpers
import { sendLinkAccountStatusToNative as sendLinkAccountStatusToNativeFunctionGenerator } from "@payment-client/helpers/WalletHelper";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

//contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { linkWalletPublic, validateLinkWalletV3 } from "@payment-client/apis/publicWallet";
import { isEmpty } from "@swiggy-private/js-utils";

interface UseWalletVerifyOtpReturn {
    verifyInProgress: boolean;
    resendInProgress: boolean;
    verifyOtp: (otp: string) => Promise<Result<boolean, { statusCode: number; statusMessage: string }>>;
    resendOtp: () => Promise<void>;
    sendLinkAccountStatusToNative?: VoidFunction;
}

interface UseWalletVerifyOtpProps {
    walletCode: WALLET_CODES;
    mobileNumber: string;
    paymentCode: string;
    isPaymentModeRoute: boolean;
}

export const useWalletVerifyOtp = ({
    walletCode,
    mobileNumber,
    paymentCode,
    isPaymentModeRoute,
}: UseWalletVerifyOtpProps): UseWalletVerifyOtpReturn => {
    const isMountedRef = useMountedRef();
    const { host } = React.useContext(ApiContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const [walletState, setWalletState] = React.useState<Option<object>>(None);
    const [verifyInProgress, setVerifyInProgress] = React.useState(false);
    const [resendInProgress, setResendInProgress] = React.useState(false);
    const sendLinkAccountStatusToNative = React.useRef<VoidFunction | undefined>();
    const publicApiConfig = useContext(PublicApiContext);

    const sendOtp = React.useCallback(async () => {
        const isPublicApiEnable = isPublicApiEnabled(
            publicApiConfig || {},
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.WALLET_APIS,
            walletCode.toLowerCase(),
        );

        try {
            const linkWalletFunction = isPublicApiEnable ? linkWalletPublic : linkWallet;
            const res = await linkWalletFunction(host.unwrap(), walletCode, mobileNumber);
            return isMountedRef.current && setWalletState(Some(res || {}));
        } catch (err) {
            return captureError(err);
        }
    }, [host, isMountedRef, mobileNumber, walletCode, publicApiConfig]);

    useEffectOnce(() => {
        sendOtp();
    });

    const resendOtp = React.useCallback(async () => {
        if (resendInProgress) {
            return;
        }

        setResendInProgress(true);
        sendOtp().then((_) => isMountedRef.current && setResendInProgress(false));
    }, [isMountedRef, resendInProgress, sendOtp]);

    const verifyOtp = React.useCallback(
        async (otp: string): Promise<Result<boolean, { statusCode: number; statusMessage: string }>> => {
            if (walletState.isNone()) {
                return Err({ statusCode: 1, statusMessage: OOPS_SOMETHING_WENT_WRONG });
            }

            setVerifyInProgress(true);
            let otpResponse = Ok<boolean, { statusCode: number; statusMessage: string }>(true);

            const isPublicApiEnable = isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.WALLET_APIS,
                walletCode.toLowerCase(),
            );

            const validateOtpFunction = isPublicApiEnable ? validateLinkWalletV3 : validateOtp;

            try {
                const response = await validateOtpFunction(host.unwrap(), {
                    walletCode,
                    otp,
                    ...walletState.unwrap(),
                });
                otpResponse = Ok(true);

                sendLinkAccountStatusToNative.current = sendLinkAccountStatusToNativeFunctionGenerator({
                    paymentCode,
                    status: "SUCCESS",
                    token: response?.accessToken,
                    source: isPaymentModeRoute ? LINK_ACCOUNT_SOURCE.ACCOUNTS : LINK_ACCOUNT_SOURCE.PAYMENTS,
                    linkedMobileNumber: mobileNumber,
                    ...(!isEmpty(paymentResponse) && {
                        tenant: paymentResponse.tenant as Tenant,
                    }),
                });
            } catch (err) {
                otpResponse = Err(err || { statusCode: 1, statusMessage: OOPS_SOMETHING_WENT_WRONG });
            }

            isMountedRef.current && setVerifyInProgress(false);
            return otpResponse;
        },
        [
            walletState,
            publicApiConfig,
            walletCode,
            isMountedRef,
            host,
            paymentCode,
            isPaymentModeRoute,
            mobileNumber,
            paymentResponse,
        ],
    );

    return {
        verifyInProgress,
        resendInProgress,
        verifyOtp,
        resendOtp,
        sendLinkAccountStatusToNative: sendLinkAccountStatusToNative.current,
    };
};
